import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import NoIndexHelmet from '../../components/seo/NoIndexHelmet';
import { useAuth } from '../../components/context/AuthContext';
import { useNotification } from '../../components/ui/Notification';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import GctSummaryBreakdown from '../../components/tax/GctSummaryBreakdown';
import { Button, Container, Row, Col, Card, ListGroup, Alert, Image, Form, FormCheck } from 'react-bootstrap';
import { useCheckoutCart } from '../../hooks/useCheckoutCart';
import { useCheckoutCatalog } from '../../hooks/useCheckoutCatalog';
import { useCheckoutDelivery } from '../../hooks/useCheckoutDelivery';
import { useCheckoutPricing } from '../../hooks/useCheckoutPricing';
import { startCheckoutPayment } from '../../services/payment/checkoutPayment';

const WIPAY_REDIRECT_RECOVERY_MS = 10000;

export default function CheckoutPage() {
  const { supermarketId, cartInstanceId } = useParams();
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState(null);
  const isProcessingRef = useRef(false);
  const simulatedTimeoutRef = useRef(null);
  const redirectRecoveryTimeoutRef = useRef(null);
  const abortedRef = useRef(false);

  const { cartInstance, loading, error } = useCheckoutCart({
    currentUser,
    supermarketId,
    cartInstanceId,
  });

  const { supermarket, productImages, productDetails, inventoryDetails } = useCheckoutCatalog({
    supermarketId,
    cartInstance,
  });

  const {
    deliveryOption,
    setDeliveryOption,
    deliveryAddress,
    setDeliveryAddress,
    deliveryPhone,
    setDeliveryPhone,
    deliveryAddressConfirmed,
    setDeliveryAddressConfirmed,
    userDeliveries,
    selectedDeliveryId,
    selectSavedDelivery,
    persistDeliveryIfNeeded,
    validateDeliveryForPayment,
  } = useCheckoutDelivery({ currentUser, supermarket });

  const {
    paymentType,
    setPaymentType,
    confirmationFee,
    gatewayPaymentType,
    wipayResponseUrl,
    wipayAccountNumber,
    wipayEnvironment,
    wipayFeeStructure,
    wipayCountryCode,
    wipayCardType,
    simulatedPaymentFails,
    calculateDealPriceForProduct,
    orderSubtotal,
    applicableDeliveryFee,
    gctSummary,
    hasConfiguredDeliveryFee,
    configuredMaxDeliveryDistance,
    fullPaymentTotal,
    confirmationPaymentTotal,
    dueLaterLabel,
  } = useCheckoutPricing({
    cartInstance,
    inventoryDetails,
    supermarket,
    deliveryOption,
  });

  useEffect(() => {
    abortedRef.current = false;
    return () => {
      abortedRef.current = true;
      if (simulatedTimeoutRef.current != null) {
        clearTimeout(simulatedTimeoutRef.current);
        simulatedTimeoutRef.current = null;
      }
      if (redirectRecoveryTimeoutRef.current != null) {
        clearTimeout(redirectRecoveryTimeoutRef.current);
        redirectRecoveryTimeoutRef.current = null;
      }
    };
  }, []);

  const breadcrumbItems = [
    { path: '/', label: 'Home' },
    { path: '/carts', label: 'Cart' },
    { label: 'Checkout Confirmation' },
  ];

  const handleProceedToPayment = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    setPendingRedirectUrl(null);
    if (redirectRecoveryTimeoutRef.current != null) {
      clearTimeout(redirectRecoveryTimeoutRef.current);
      redirectRecoveryTimeoutRef.current = null;
    }

    const result = await startCheckoutPayment({
      currentUser,
      cartInstance,
      supermarketId,
      cartInstanceId,
      supermarket,
      confirmationFee,
      gatewayPaymentType,
      paymentType,
      orderSubtotal,
      applicableDeliveryFee,
      configuredMaxDeliveryDistance,
      gctSummary,
      deliveryOption,
      deliveryAddress,
      deliveryPhone,
      validateDeliveryForPayment,
      persistDeliveryIfNeeded,
      hasConfiguredDeliveryFee,
      wipayAccountNumber,
      wipayEnvironment,
      wipayFeeStructure,
      wipayResponseUrl,
      wipayCountryCode,
      wipayCardType,
      simulatedPaymentFails,
      navigate,
      scheduleSimulatedPayment: (fn, delayMs) => {
        const id = setTimeout(async () => {
          if (abortedRef.current) return;
          try {
            await fn();
          } finally {
            if (!abortedRef.current) {
              isProcessingRef.current = false;
              setIsProcessing(false);
            }
          }
        }, delayMs);
        simulatedTimeoutRef.current = id;
        return id;
      },
    });

    if (result.notification) {
      showNotification(result.notification);
    }

    if (result.status === 'scheduled') {
      // Processing flag cleared when the scheduled callback finishes (or unmount aborts).
      return;
    }

    if (result.status === 'redirecting') {
      // Recover if the browser blocks/stalls the WiPay navigation.
      if (result.redirectUrl) {
        setPendingRedirectUrl(result.redirectUrl);
      }
      redirectRecoveryTimeoutRef.current = setTimeout(() => {
        if (abortedRef.current) return;
        isProcessingRef.current = false;
        setIsProcessing(false);
        showNotification({
          type: 'warning',
          title: 'Redirect delayed',
          message:
            'If you were not taken to the payment page, use the link below to continue.',
          duration: 8000,
        });
      }, WIPAY_REDIRECT_RECOVERY_MS);
      return;
    }

    isProcessingRef.current = false;
    setIsProcessing(false);
  }, [
    currentUser,
    cartInstance,
    supermarketId,
    cartInstanceId,
    supermarket,
    confirmationFee,
    gatewayPaymentType,
    paymentType,
    orderSubtotal,
    applicableDeliveryFee,
    configuredMaxDeliveryDistance,
    gctSummary,
    deliveryOption,
    deliveryAddress,
    deliveryPhone,
    validateDeliveryForPayment,
    persistDeliveryIfNeeded,
    hasConfiguredDeliveryFee,
    wipayAccountNumber,
    wipayEnvironment,
    wipayFeeStructure,
    wipayResponseUrl,
    wipayCountryCode,
    wipayCardType,
    simulatedPaymentFails,
    navigate,
    showNotification,
  ]);

  if (loading) {
    return (
      <Layout>
        <Container className="mt-4 text-center">
          <p>Loading checkout...</p>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <NoIndexHelmet title="Checkout | Pack My Cart" path="/checkout" />
      <Container className="mt-4">
        <Row className="align-items-center mb-1">
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => navigate('/carts')} className="mb-2">
              &larr; Back to Cart
            </Button>
          </Col>
          <Col>
            <BreadcrumbNavigation items={breadcrumbItems} />
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" className="mt-3">
            {error} <Link to="/carts">Return to Cart</Link>
          </Alert>
        )}

        {pendingRedirectUrl && !isProcessing && (
          <Alert variant="warning" className="mt-3">
            Payment redirect did not complete.{' '}
            <a href={pendingRedirectUrl} rel="noopener noreferrer">
              Click here if you weren&apos;t redirected
            </a>
            .
          </Alert>
        )}

        {!error && !cartInstance && !loading && (
          <Alert variant="warning" className="mt-3">
            Cart instance not found or is not valid for checkout. <Link to="/carts">Return to Cart</Link>
          </Alert>
        )}

        {cartInstance && cartInstance.products && cartInstance.products.length > 0 && (
          <>
            <h2 className="mt-3 mb-3">
              Checkout Confirmation: {cartInstance.supermarketName || 'Supermarket'}
              {deliveryOption === 'delivery' && ' (Delivery)'}
              (Cart: {cartInstanceId.replace('cart_instance_', '')})
            </h2>

            {supermarket?.delivery_enabled && (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-truck me-2"></i>
                    Delivery Options
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <FormCheck
                      type="radio"
                      id="pickup-option"
                      name="deliveryOption"
                      label="Pickup at Supermarket"
                      checked={deliveryOption === 'pickup'}
                      onChange={() => setDeliveryOption('pickup')}
                      className="mb-2"
                    />
                    <FormCheck
                      type="radio"
                      id="delivery-option"
                      name="deliveryOption"
                      label="Home Delivery"
                      checked={deliveryOption === 'delivery'}
                      onChange={() => setDeliveryOption('delivery')}
                      className="mb-2"
                    />
                  </div>

                  {deliveryOption === 'delivery' && (
                    <div className="border rounded p-3 bg-light">
                      <div className="mb-3">
                        <div className="alert alert-info mb-3">
                          <strong>Delivery Fee Information:</strong>
                          <br />
                          {hasConfiguredDeliveryFee ? (
                            <>
                              A delivery fee of{' '}
                              <strong>${parseFloat(supermarket.delivery_fee).toFixed(2)}</strong> will
                              be added to your checkout total.
                              {configuredMaxDeliveryDistance != null && (
                                <>
                                  {' '}
                                  Delivery is available within{' '}
                                  <strong>{configuredMaxDeliveryDistance} km</strong> of the
                                  supermarket.
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              This supermarket offers delivery service. You will need to arrange the
                              delivery fee directly with the supermarket. The fee will be determined
                              by the supermarket based on your location and order size.
                            </>
                          )}
                        </div>

                        {supermarket?.delivery_phone && (
                          <div className="alert alert-success mb-3">
                            <strong>Supermarket Delivery Contact:</strong>
                            <br />
                            <i className="fas fa-phone me-2"></i>
                            <a
                              href={`tel:${supermarket.delivery_phone}`}
                              className="text-decoration-none fw-bold"
                            >
                              {supermarket.delivery_phone}
                            </a>
                            <br />
                            <small className="text-muted">
                              {hasConfiguredDeliveryFee
                                ? 'Contact this number for delivery questions or addresses outside the delivery radius.'
                                : 'Contact this number to arrange delivery details and fees.'}
                            </small>
                          </div>
                        )}

                        <FormCheck
                          type="checkbox"
                          id="delivery-confirmation"
                          label={
                            hasConfiguredDeliveryFee
                              ? `I understand that a delivery fee of $${parseFloat(supermarket.delivery_fee).toFixed(2)}${
                                  configuredMaxDeliveryDistance != null
                                    ? ` applies for deliveries within ${configuredMaxDeliveryDistance} km`
                                    : ' applies to this order'
                                }`
                              : 'I understand that I need to arrange the delivery fee directly with the supermarket'
                          }
                          checked={deliveryAddressConfirmed}
                          onChange={(e) => setDeliveryAddressConfirmed(e.target.checked)}
                          className="mb-3"
                        />
                      </div>

                      {deliveryAddressConfirmed && (
                        <div>
                          <h6>Delivery Address</h6>

                          {userDeliveries.length > 0 ? (
                            <div className="mb-3">
                              <Form.Label>Select a saved address or enter a new one:</Form.Label>
                              <Form.Select
                                value={selectedDeliveryId || ''}
                                onChange={(e) => selectSavedDelivery(e.target.value)}
                              >
                                <option value="">Enter new address</option>
                                {userDeliveries.map((delivery) => (
                                  <option key={delivery.id} value={delivery.id}>
                                    {delivery.address}{' '}
                                    {delivery.phone ? `(${delivery.phone})` : ''}
                                    {delivery.isDefault ? ' (Default)' : ''}
                                  </option>
                                ))}
                              </Form.Select>
                              <small className="text-muted">
                                Found {userDeliveries.length} saved address
                                {userDeliveries.length > 1 ? 'es' : ''}
                              </small>
                            </div>
                          ) : null}

                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Delivery Address:</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={deliveryAddress}
                              onChange={(e) => setDeliveryAddress(e.target.value)}
                              placeholder="Enter your complete delivery address..."
                              required={deliveryOption === 'delivery'}
                              className="w-100"
                              style={{
                                fontSize: '1rem',
                                padding: '0.75rem',
                                borderRadius: '0.375rem',
                                resize: 'vertical',
                              }}
                            />
                            <Form.Text className="text-muted small">
                              Please provide a complete address including street, city, and any
                              relevant landmarks.
                            </Form.Text>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Delivery Phone Number:</Form.Label>
                            <Form.Control
                              type="tel"
                              value={deliveryPhone}
                              onChange={(e) => setDeliveryPhone(e.target.value)}
                              placeholder="Enter your phone number for delivery contact"
                              required={deliveryOption === 'delivery'}
                              className="w-100"
                              style={{
                                fontSize: '1rem',
                                padding: '0.75rem',
                                borderRadius: '0.375rem',
                              }}
                            />
                            <Form.Text className="text-muted small">
                              This number will be used by the supermarket to contact you regarding
                              delivery.
                            </Form.Text>
                          </Form.Group>
                        </div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-light border-bottom">
                <h4 className="mb-0 text-dark fw-bold">
                  <i className="fas fa-shopping-cart me-2 text-primary"></i>
                  Order Summary
                </h4>
              </Card.Header>
              <ListGroup variant="flush">
                {cartInstance.products.map((product) => {
                  const productDetail = productDetails[product.productId];
                  const productImage = productImages[product.productId];
                  const dealCalculation = calculateDealPriceForProduct(
                    product.productId,
                    product.quantity,
                    product.price || 0
                  );
                  const unitPrice = product.price !== undefined ? product.price : 0;

                  return (
                    <ListGroup.Item key={product.productId} className="p-4 border-bottom">
                      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                        <div
                          className="text-center text-md-start"
                          style={{ width: '100px', height: '100px', flexShrink: 0 }}
                        >
                          {productImage ? (
                            <Image
                              src={productImage}
                              alt={product.name || 'Product'}
                              className="img-fluid rounded shadow-sm"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center bg-light rounded shadow-sm h-100 border"
                              style={{
                                fontSize: '0.9rem',
                                color: '#6c757d',
                              }}
                            >
                              <i className="fas fa-image text-muted"></i>
                            </div>
                          )}
                        </div>

                        <div className="flex-grow-1">
                          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-2">
                            <div className="flex-grow-1">
                              <h5 className="mb-1 fw-bold text-dark">
                                {product.name || productDetail?.display_name || 'Product'}
                              </h5>
                              {productDetail?.description && (
                                <p className="mb-1 small text-muted text-break">
                                  {productDetail.description.length > 80
                                    ? `${productDetail.description.substring(0, 80)}...`
                                    : productDetail.description}
                                </p>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-secondary fs-6 px-3 py-2">
                                <i className="fas fa-boxes me-1"></i>
                                Qty: {product.quantity}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="text-end text-md-end" style={{ minWidth: '120px' }}>
                          <div className="d-flex flex-column align-items-end">
                            {dealCalculation.dealApplied ? (
                              <>
                                <h5 className="mb-1 fw-bold text-success">
                                  ${dealCalculation.price.toFixed(2)}
                                </h5>
                                <small className="text-muted text-decoration-line-through">
                                  ${(unitPrice * product.quantity).toFixed(2)}
                                </small>
                                <small className="text-success fw-bold">
                                  Save ${dealCalculation.savings.toFixed(2)}
                                </small>
                              </>
                            ) : (
                              <>
                                <h5 className="mb-1 fw-bold text-success">
                                  ${dealCalculation.price.toFixed(2)}
                                </h5>
                                <small className="text-muted">${unitPrice.toFixed(2)} each</small>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
              <Card.Footer className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0 text-dark">Order Subtotal</h5>
                    <small className="text-muted">
                      {deliveryOption === 'delivery' ? 'Pay on delivery' : 'Pay at pickup'}
                    </small>
                  </div>
                  <div className="text-end">
                    <h4 className="mb-0 fw-bold text-primary">${orderSubtotal.toFixed(2)}</h4>
                  </div>
                </div>
                {applicableDeliveryFee > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                    <div>
                      <h6 className="mb-0 text-dark">Delivery Fee</h6>
                      <small className="text-muted">
                        Pay now
                        {configuredMaxDeliveryDistance != null
                          ? ` (within ${configuredMaxDeliveryDistance} km)`
                          : ''}
                      </small>
                    </div>
                    <div className="text-end">
                      <h5 className="mb-0 fw-bold text-info">
                        ${applicableDeliveryFee.toFixed(2)}
                      </h5>
                    </div>
                  </div>
                )}
                <GctSummaryBreakdown taxSummary={gctSummary} className="mt-3 pt-3 border-top" />
              </Card.Footer>
            </Card>

            <Card className="mb-5">
              <Card.Header>
                <h4>Payment Options</h4>
              </Card.Header>
              <Card.Body>
                {supermarket?.full_payment_enabled ? (
                  <>
                    <div className="mb-3">
                      <FormCheck
                        type="radio"
                        id="confirmation-fee-only"
                        name="paymentType"
                        label={`Pay Confirmation Fee Only ($${confirmationPaymentTotal.toFixed(2)}${applicableDeliveryFee > 0 ? ` incl. $${applicableDeliveryFee.toFixed(2)} delivery` : ''})`}
                        checked={paymentType === 'confirmation_fee_only'}
                        onChange={() => setPaymentType('confirmation_fee_only')}
                        className="mb-2"
                      />
                      <Form.Text className="text-muted small d-block mb-3">
                        Pay the confirmation fee now
                        {applicableDeliveryFee > 0
                          ? ` plus a $${applicableDeliveryFee.toFixed(2)} delivery fee`
                          : ''}
                        . Order subtotal of ${orderSubtotal.toFixed(2)} will be due{' '}
                        {dueLaterLabel === 'on delivery'
                          ? 'upon delivery'
                          : 'at the supermarket upon pickup'}
                        .
                      </Form.Text>
                      <FormCheck
                        type="radio"
                        id="full-payment"
                        name="paymentType"
                        label={`Pay Full Amount ($${fullPaymentTotal.toFixed(2)})`}
                        checked={paymentType === 'full'}
                        onChange={() => setPaymentType('full')}
                        className="mb-2"
                      />
                      <Form.Text className="text-muted small d-block mb-3">
                        Pay the full order total now. The confirmation fee ($
                        {confirmationFee.toFixed(2)}) will be collected as a processing fee
                        {applicableDeliveryFee > 0
                          ? ` and includes a $${applicableDeliveryFee.toFixed(2)} delivery fee`
                          : ''}
                        .
                      </Form.Text>
                    </div>
                    <div className="mb-3 p-3 bg-light rounded">
                      <p className="mb-1">
                        <strong>Order Subtotal:</strong> ${orderSubtotal.toFixed(2)}
                      </p>
                      <GctSummaryBreakdown taxSummary={gctSummary} className="mb-2" compact />
                      {applicableDeliveryFee > 0 && (
                        <p className="mb-1">
                          <strong>Delivery Fee:</strong> ${applicableDeliveryFee.toFixed(2)}
                        </p>
                      )}
                      <p className="mb-1">
                        <strong>Confirmation Fee:</strong> ${confirmationFee.toFixed(2)}{' '}
                        {paymentType === 'full' ? '(as processing fee)' : ''}
                      </p>
                      <hr className="my-2" />
                      <p className="mb-0">
                        <strong>Total to Pay:</strong>{' '}
                        <span className="text-success fw-bold">
                          $
                          {(paymentType === 'full'
                            ? fullPaymentTotal
                            : confirmationPaymentTotal
                          ).toFixed(2)}
                        </span>
                      </p>
                    </div>
                    <Button
                      className="btn btn-primary btn-lg w-100"
                      onClick={handleProceedToPayment}
                      disabled={isProcessing || confirmationFee <= 0}
                    >
                      {isProcessing
                        ? 'Processing...'
                        : confirmationFee > 0
                          ? `Proceed to Pay $${(paymentType === 'full' ? fullPaymentTotal : confirmationPaymentTotal).toFixed(2)}`
                          : 'Fee Not Set'}
                    </Button>
                  </>
                ) : (
                  <>
                    <p>
                      To confirm your order, please pay the non-refundable confirmation fee
                      {applicableDeliveryFee > 0 ? ' and delivery fee' : ''}. The order subtotal will
                      be due{' '}
                      {dueLaterLabel === 'on delivery'
                        ? 'upon delivery'
                        : 'at the supermarket upon pickup'}
                      .
                    </p>
                    <p>
                      <strong>Order Subtotal:</strong> ${orderSubtotal.toFixed(2)} (Due{' '}
                      {dueLaterLabel})
                    </p>
                    <GctSummaryBreakdown taxSummary={gctSummary} className="mb-3" compact />
                    {applicableDeliveryFee > 0 && (
                      <p>
                        <strong>Delivery Fee:</strong> ${applicableDeliveryFee.toFixed(2)} (Payable
                        now)
                      </p>
                    )}
                    <p>
                      <strong>Confirmation Fee:</strong> ${confirmationFee.toFixed(2)} (Payable now)
                    </p>
                    <Button
                      className="btn btn-primary btn-lg w-100"
                      onClick={handleProceedToPayment}
                      disabled={isProcessing || confirmationFee <= 0}
                    >
                      {isProcessing
                        ? 'Processing...'
                        : confirmationFee > 0
                          ? `Proceed to Pay $${confirmationPaymentTotal.toFixed(2)}`
                          : 'Fee Not Set'}
                    </Button>
                  </>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </Layout>
  );
}

// TODO: Removing zero-inventory products from all carts should be done in a Cloud Function with admin privileges.
// Frontend can only update the current user's cart due to Firestore security rules.
